/**
 * « Ajouter un utilisateur » — l'onglet Rôles, côté arrivées.
 *
 * Les comptes ont été créés en masse une fois, depuis l'annuaire Entra, pour la
 * migration Clockify. Ce n'est pas un mode de fonctionnement : au quotidien
 * c'est un admin qui décide, et il lui fallait un bouton plutôt qu'un script.
 *
 * Ce panneau ne crée pas de compte — il pose une invitation. `users/{oid}` est
 * keyé par l'oid Entra, que personne ne connaît avant la première connexion ;
 * kronos-api convertit l'invitation en compte au premier jeton portant ce
 * même e-mail (`actions/provisionUser.ts`), et la fait disparaître d'ici.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import { useTranslation } from 'react-i18next';
import type { UserInvite } from '@altyor-kronos/shared-types';
import { useCollectionSnapshot } from '../../core/data/useCollectionSnapshot';
import { createUserInvite, deleteUserInvite } from '../../core/data/adminMutations';

/** Même exigence que le serveur (`isValidUserInvite`) : un e-mail exploitable. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  departmentOptions: { id: string; name: string }[];
  /** E-mails des comptes existants, pour ne pas inviter quelqu'un qui est déjà là. */
  knownEmails: Set<string>;
}

export default function UserInvitesPanel({ departmentOptions, knownEmails }: Props) {
  const { t } = useTranslation();
  const { rows, error } = useCollectionSnapshot<UserInvite>('userInvites');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', departmentId: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const pendingEmails = useMemo(
    () => new Set(rows.map((r) => (r.data.email ?? '').toLowerCase())),
    [rows],
  );

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    setOpen(false);
    setForm({ firstName: '', lastName: '', email: '', departmentId: '' });
    setFormError(null);
  };

  const submit = async () => {
    const email = form.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setFormError(t('admin.roles.invites.errors.email')); return; }
    if (!form.firstName.trim() && !form.lastName.trim()) {
      setFormError(t('admin.roles.invites.errors.name')); return;
    }
    if (knownEmails.has(email)) { setFormError(t('admin.roles.invites.errors.exists')); return; }
    if (pendingEmails.has(email)) { setFormError(t('admin.roles.invites.errors.pending')); return; }
    setSaving(true);
    setFormError(null);
    try {
      await createUserInvite({ ...form, email });
      close();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Button
        variant="contained"
        startIcon={<PersonAddIcon />}
        onClick={() => setOpen(true)}
        data-testid="add-user-button"
      >
        {t('admin.roles.invites.add')}
      </Button>

      {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 0 }}>{error.message}</Alert>}

      {rows.length > 0 && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 0 }} icon={false}>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('admin.roles.invites.pendingHelp')}</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {rows.map(({ id, data }) => (
              <Chip
                key={id}
                label={`${data.firstName ?? ''} ${data.lastName ?? ''} — ${data.email}`.trim()}
                onDelete={() => void deleteUserInvite(id)}
                sx={{ borderRadius: 0 }}
                data-testid={`invite-chip-${data.email}`}
              />
            ))}
          </Stack>
        </Alert>
      )}

      <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>{t('admin.roles.invites.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.roles.invites.dialogHelp')}
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('admin.roles.columns.firstName')} value={form.firstName}
              onChange={set('firstName')} size="small" autoFocus
              slotProps={{ htmlInput: { 'data-testid': 'invite-firstName' } }}
            />
            <TextField
              label={t('admin.roles.columns.lastName')} value={form.lastName}
              onChange={set('lastName')} size="small"
              slotProps={{ htmlInput: { 'data-testid': 'invite-lastName' } }}
            />
            <TextField
              label={t('admin.roles.columns.email')} value={form.email}
              onChange={set('email')} size="small" type="email"
              slotProps={{ htmlInput: { 'data-testid': 'invite-email' } }}
            />
            <TextField
              select label={t('admin.roles.columns.group')} value={form.departmentId}
              onChange={set('departmentId')} size="small"
            >
              <MenuItem value=""><em>{t('admin.roles.noGroup')}</em></MenuItem>
              {departmentOptions.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>
          </Stack>
          {formError && <Alert severity="error" sx={{ mt: 2, borderRadius: 0 }}>{formError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={() => void submit()} disabled={saving} data-testid="invite-submit">
            {t('admin.roles.invites.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
